import PublicLayout from "@/components/PublicLayout";
import { Link, useNavigate } from "react-router-dom";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { useState, useEffect } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Mail, Smartphone, ShieldCheck, ArrowLeft, RefreshCw } from "lucide-react";
// import GoogleSignInButton from "@/components/GoogleSignInButton"; // temporarily disabled
import PasswordInput from "@/components/PasswordInput";

export default function Register() {
  const { authenticateFromToken } = useAuth();
  const nav = useNavigate();
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [resending, setResending] = useState(false);
  const [form, setForm] = useState({ full_name: "", email: "", mobile: "", password: "", confirm: "" });
  const [otpTab, setOtpTab] = useState("mobile");
  const [otpCode, setOtpCode] = useState("");
  const [session, setSession] = useState(null); // { session_id, email_masked, mobile_masked, dev_mobile_otp }
  const [countdown, setCountdown] = useState(0);
  const [resendCooldown, setResendCooldown] = useState(0);

  useEffect(() => {
    if (countdown <= 0) return;
    const t = setInterval(() => setCountdown((c) => Math.max(0, c - 1)), 1000);
    return () => clearInterval(t);
  }, [countdown]);

  useEffect(() => {
    if (resendCooldown <= 0) return;
    const t = setInterval(() => setResendCooldown((c) => Math.max(0, c - 1)), 1000);
    return () => clearInterval(t);
  }, [resendCooldown]);

  const startOtp = async (e) => {
    e.preventDefault();
    if (form.password.length < 6) return toast.error("Password must be at least 6 characters");
    if (form.password !== form.confirm) return toast.error("Passwords do not match");
    if (!/^\d{10}$/.test(form.mobile.replace(/\D/g, ""))) return toast.error("Enter a valid 10-digit mobile number");
    setLoading(true);
    try {
      const { data } = await api.post("/auth/send-otps", {
        full_name: form.full_name.trim(),
        email: form.email.trim().toLowerCase(),
        mobile: form.mobile.replace(/\D/g, ""),
      });
      setSession(data);
      setCountdown(600);
      setResendCooldown(30);
      setStep(2);
      toast.success("OTPs sent to your email and mobile");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not send OTPs");
    } finally {
      setLoading(false);
    }
  };

  const verify = async (e) => {
    e.preventDefault();
    if (otpCode.length < 4) return toast.error("Enter the OTP code");
    setVerifying(true);
    try {
      const { data } = await api.post("/auth/register-verify", {
        session_id: session.session_id,
        otp_type: otpTab,
        otp_code: otpCode,
        password: form.password,
      });
      authenticateFromToken(data.token, data.user);
      toast.success(`Account created! Verified via ${data.verified_via}.`);
      nav("/dashboard", { replace: true });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Verification failed");
    } finally {
      setVerifying(false);
    }
  };

  const resend = async () => {
    if (!session) return;
    if (resendCooldown > 0) return;
    setResending(true);
    try {
      const { data } = await api.post("/auth/resend-otp", {
        session_id: session.session_id,
        channel: otpTab,
      });
      if (data.dev_mobile_otp) {
        setSession((s) => ({ ...s, dev_mobile_otp: data.dev_mobile_otp }));
      }
      setCountdown(600);
      setResendCooldown(30);
      toast.success(`New ${otpTab} OTP sent`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Resend failed");
    } finally {
      setResending(false);
    }
  };

  const mm = Math.floor(countdown / 60);
  const ss = String(countdown % 60).padStart(2, "0");

  return (
    <PublicLayout>
      <section className="py-16 gradient-radial-blue">
        <div className="max-w-md mx-auto px-4">
          <div className="rounded-2xl border border-slate-200 bg-white p-8 shadow-sm">
            {step === 1 && (
              <>
                <div className="section-label">Get Started</div>
                <h1 className="mt-2 font-display font-bold text-3xl text-[#0A192F]">Create your account</h1>
                <p className="text-sm text-slate-600 mt-1">We'll send verification codes to both your email and mobile. You only need to verify one to finish.</p>

                <form onSubmit={startOtp} className="mt-6 space-y-4" data-testid="register-form">
                  <div>
                    <Label>Full Name</Label>
                    <Input required value={form.full_name} onChange={(e) => setForm({ ...form, full_name: e.target.value })} data-testid="register-name" />
                  </div>
                  <div>
                    <Label>Email</Label>
                    <Input type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="register-email" />
                  </div>
                  <div>
                    <Label>Mobile Number</Label>
                    <div className="flex" data-testid="register-mobile-group">
                      <span className="inline-flex items-center px-3 rounded-l-md border border-r-0 border-input bg-slate-50 text-slate-700 text-sm font-medium">+91</span>
                      <Input
                        required
                        value={form.mobile}
                        onChange={(e) => setForm({ ...form, mobile: e.target.value.replace(/\D/g, "").slice(0, 10) })}
                        placeholder="9876543210"
                        inputMode="numeric"
                        maxLength={10}
                        className="rounded-l-none"
                        data-testid="register-mobile"
                      />
                    </div>
                    <div className="text-xs text-slate-500 mt-1">10-digit Indian mobile. We'll SMS your OTP here.</div>
                  </div>
                  <div>
                    <Label>Password</Label>
                    <PasswordInput required value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="register-password" />
                    <div className="text-xs text-slate-500 mt-1">Minimum 6 characters.</div>
                  </div>
                  <div>
                    <Label>Confirm Password</Label>
                    <PasswordInput required value={form.confirm} onChange={(e) => setForm({ ...form, confirm: e.target.value })} data-testid="register-confirm-password" />
                  </div>
                  <Button type="submit" disabled={loading} className="w-full bg-[#1E3A8A] hover:bg-[#1E40AF] h-11" data-testid="register-continue-btn">
                    {loading ? "Sending OTPs…" : "Send verification codes"}
                  </Button>
                </form>
                <div className="mt-5 text-center text-sm text-slate-600">
                  Already have an account?{" "}
                  <Link to="/login" className="text-[#1E3A8A] font-medium hover:underline">Sign in</Link>
                </div>
              </>
            )}

            {step === 2 && session && (
              <>
                <button onClick={() => { setStep(1); setOtpCode(""); }} className="flex items-center gap-1 text-xs text-slate-500 hover:text-[#1E3A8A]" data-testid="register-back-btn">
                  <ArrowLeft className="w-3.5 h-3.5" /> Edit details
                </button>
                <div className="section-label mt-3">Verify your account</div>
                <h1 className="mt-2 font-display font-bold text-2xl text-[#0A192F]">Enter one OTP</h1>
                <p className="text-sm text-slate-600 mt-1">
                  We sent codes to <span className="font-medium">{session.email_masked}</span> and <span className="font-medium">+91 {session.mobile_masked}</span>. Verify either one to activate your account.
                </p>

                <Tabs value={otpTab} onValueChange={(v) => { setOtpTab(v); setOtpCode(""); }} className="mt-6" data-testid="otp-tabs">
                  <TabsList className="grid grid-cols-2 w-full">
                    <TabsTrigger value="mobile" data-testid="otp-tab-mobile"><Smartphone className="w-4 h-4 mr-1.5" /> Mobile</TabsTrigger>
                    <TabsTrigger value="email" data-testid="otp-tab-email"><Mail className="w-4 h-4 mr-1.5" /> Email</TabsTrigger>
                  </TabsList>

                  <TabsContent value="email" className="mt-4">
                    <div className="text-xs text-slate-500 mb-2">Enter the 6-digit code from your email inbox.</div>
                  </TabsContent>
                  <TabsContent value="mobile" className="mt-4">
                    <div className="text-xs text-slate-500 mb-2">Enter the 6-digit code sent to your mobile.</div>
                    {session.dev_mobile_otp && (
                      <div className="mb-3 p-3 rounded-md border border-amber-200 bg-amber-50 text-amber-900 text-xs" data-testid="dev-mobile-otp-banner">
                        <div className="font-semibold flex items-center gap-1.5"><ShieldCheck className="w-3.5 h-3.5" /> Test Mode</div>
                        SMS provider not configured. Your mobile OTP is: <span className="font-mono font-bold text-sm text-[#0A192F]" data-testid="dev-mobile-otp-value">{session.dev_mobile_otp}</span>
                      </div>
                    )}
                  </TabsContent>
                </Tabs>

                <form onSubmit={verify} className="mt-4 space-y-4" data-testid="otp-verify-form">
                  <Input
                    required
                    value={otpCode}
                    onChange={(e) => setOtpCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                    placeholder="6-digit code"
                    inputMode="numeric"
                    className="text-center text-2xl font-mono tracking-[0.4em] h-14"
                    data-testid="otp-code-input"
                  />
                  <Button type="submit" disabled={verifying} className="w-full bg-[#1E3A8A] hover:bg-[#1E40AF] h-11" data-testid="otp-verify-btn">
                    {verifying ? "Verifying…" : "Verify & Create Account"}
                  </Button>
                </form>

                <div className="mt-4 flex items-center justify-between text-xs text-slate-500">
                  <span>{countdown > 0 ? `Code expires in ${mm}:${ss}` : "Code expired"}</span>
                  <button
                    type="button"
                    onClick={resend}
                    disabled={resending || resendCooldown > 0}
                    className="text-[#1E3A8A] font-medium hover:underline disabled:text-slate-400 disabled:no-underline inline-flex items-center gap-1"
                    data-testid="otp-resend-btn"
                  >
                    <RefreshCw className="w-3 h-3" /> {resending ? "Sending…" : resendCooldown > 0 ? `Resend in ${resendCooldown}s` : "Resend"}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      </section>
    </PublicLayout>
  );
}
